import type { ShiftCardVariant } from "@/components/shifts/shift-card";
import type { Shift, ShiftStatus } from "@/domain";
import type { ProfessionalApplicationStatus, ProfessionalShiftStatus } from "@/domain/professionals";
import {
  SHIFT_POST_STATUS_BADGE,
  SHIFT_STATUS_VALUES,
  type ShiftPostStatus,
  type ShiftPostStatusBadgeStyle
} from "@/domain/shifts";
import type { ShiftListItem } from "@/domain/practices";
import { formatDate } from "./date-utils";

export function getShiftStatusLabel(status: ShiftStatus, shift: Shift): string {
  switch (status) {
    case "active":
      return shift.createdAt
        ? `Active since ${formatDate(shift.createdAt, "DATE_DISPLAY")}`
        : "Active";
    case "upcoming":
      return "Upcoming";
    case "completed":
      return shift.completedAt
        ? `Completed on ${formatDate(shift.completedAt, "DATE_DISPLAY")}`
        : "Completed";
    case "cancelled":
      return shift.cancelledAt
        ? `Cancelled on ${formatDate(shift.cancelledAt, "DATE_DISPLAY")}`
        : "Cancelled";
    case "draft":
      return "Drafted";
    case "deleted":
      return "Deleted";
    default:
      return "";
  }
}

export function formatPricingType(type: string): string {
  switch (type) {
    case "fixed_amount":
      return "Fixed Amount";
    case "working_hours":
      return "Per Working Hour";
    case "total_hours":
      return "Per Total Hour";
    default:
      return type;
  }
}

export function parseShiftPostStatus(raw: string): ShiftPostStatus | null {
  return raw in SHIFT_POST_STATUS_BADGE ? (raw as ShiftPostStatus) : null;
}

export function getShiftPostStatusBadge(raw: string): ShiftPostStatusBadgeStyle {
  const parsed = parseShiftPostStatus(raw);
  return parsed
    ? SHIFT_POST_STATUS_BADGE[parsed]
    : { label: raw, className: "bg-muted text-muted-foreground" };
}

export function parseShiftStatusFromApi(raw: string): ShiftStatus | null {
  return (SHIFT_STATUS_VALUES as readonly string[]).includes(raw)
    ? (raw as ShiftStatus)
    : null;
}

/** Short label for aggregate shift status chips (no dates; use {@link getShiftStatusLabel} for full shift rows). */
/** Map aggregate {@link ShiftStatus} to {@link ShiftCardVariant} for practice shift list cards. */
export function shiftStatusToShiftCardVariant(status: ShiftStatus): ShiftCardVariant {
  switch (status) {
    case "active":
      return "active";
    case "upcoming":
      return "upcoming";
    case "completed":
      return "completed";
    case "cancelled":
      return "cancelled";
    case "draft":
      return "draft";
    default:
      return "default";
  }
}

/**
 * Earliest shift post date for custom shifts (card summaries), or fixed copy for recurring / none.
 * @see GetPracticeShifts — posts are ordered by date in payloads; this re-sorts defensively.
 */
export function getShiftCardSummaryScheduleDate(
  shift: Pick<ShiftListItem, "shiftTimeType" | "nextShiftDate">
): string {
  if (shift.shiftTimeType === "custom" && shift.nextShiftDate) {
    return formatDate(shift.nextShiftDate, "MONTH_DAY_COMMA_YEAR");
  }
  if (shift.shiftTimeType === "recurring") {
    return "Recurring";
  }
  return "\u2014";
}

/** Time range from the earliest post for custom shifts; null if not applicable. */
export function getShiftCardSummaryScheduleTime(
  shift: Pick<
    ShiftListItem,
    "shiftTimeType" | "nextShiftStart" | "nextShiftEnd"
  >
): string | null {
  if (
    shift.shiftTimeType === "custom" &&
    shift.nextShiftStart &&
    shift.nextShiftEnd
  ) {
    return `${shift.nextShiftStart} \u2013 ${shift.nextShiftEnd}`;
  }
  return null;
}

export function getShiftStatusShortLabel(status: ShiftStatus): string {
  switch (status) {
    case "active":
      return "Active";
    case "upcoming":
      return "Upcoming";
    case "completed":
      return "Completed";
    case "cancelled":
      return "Cancelled";
    case "draft":
      return "Draft";
    case "deleted":
      return "Deleted";
    default:
      return status;
  }
}

export function getApplicationDisplayStatus(
  status: ProfessionalApplicationStatus
): { label: string; colorStatus: ProfessionalShiftStatus } {
  switch (status) {
    case "withdrawn":
      return { label: "Withdrawn", colorStatus: "withdrawn" };
    case "draft":
      return { label: "Draft", colorStatus: "applied" };
    case "applied":
      return { label: "Pending", colorStatus: "applied" };
    case "nominated":
      return { label: "Nominated", colorStatus: "upcoming" };
    case "not_nominated":
      return { label: "Not Selected", colorStatus: "withdrawn" };
    case "selected":
      return { label: "Selected", colorStatus: "active" };
    default:
      return { label: "Unknown", colorStatus: "withdrawn" };
  }
}
